import { v4 as uuidv4 } from "uuid";

export const DUST_ANONYMOUS_ID_COOKIE = "_dust_aid";

const ANONYMOUS_ID_MAX_AGE_SECONDS = 31536000; // 1 year

/**
 * Returns the cookie domain for cross-subdomain sharing, derived from the
 * current hostname (e.g. `.dasis.ai` on www/app.dasis.ai) so the anonymous id
 * is shared between the marketing site and the app. Hardcoding a domain here
 * broke on forks: a Domain attribute that doesn't match the current host makes
 * the browser silently reject the cookie (fresh id on every load).
 * On localhost/IP hosts returns null (no domain attribute needed).
 */
export function getRootCookieDomain(): string | null {
  if (typeof window === "undefined") {
    return null;
  }

  const { hostname } = window.location;
  if (hostname === "localhost" || /^[\d.]+$/.test(hostname)) {
    return null;
  }

  const labels = hostname.split(".");
  if (labels.length < 2) {
    return null;
  }

  // Registrable-domain approximation: the last two labels. Correct for our
  // domains (dasis.ai, dust.tt); revisit if we ever serve from a public-suffix
  // domain like *.co.kr.
  return `.${labels.slice(-2).join(".")}`;
}

/**
 * Returns the root cookie domain for PostHog cross-subdomain tracking.
 * On production returns the site's root domain (e.g. `.dasis.ai`); on
 * localhost/dev returns `undefined`.
 */
export function getPostHogCookieDomain(): string | undefined {
  const domain = getRootCookieDomain();
  return domain ?? undefined;
}

/**
 * Builds the cookie string for `_dust_aid` including the domain attribute
 * when applicable (production multi-subdomain setup).
 */
export function buildDustAidCookieString(value: string): string {
  const domain = getRootCookieDomain();
  const domainPart = domain ? `; domain=${domain}` : "";
  return `${DUST_ANONYMOUS_ID_COOKIE}=${value}; path=/${domainPart}; SameSite=Lax; Secure; max-age=${ANONYMOUS_ID_MAX_AGE_SECONDS}`;
}

/**
 * Reads the `_dust_aid` cookie from `document.cookie`; if absent, generates a
 * UUIDv4, sets it as a first-party cookie, and returns it.
 *
 * The cookie is set with the site's root domain (derived at runtime, e.g.
 * `domain=.dasis.ai`) so it is shared between the marketing site and the app.
 *
 * Client-side only. Returns `null` when called outside a browser context.
 */
export function getOrCreateAnonymousId(): string | null {
  if (typeof document === "undefined") {
    return null;
  }

  const existing = readAnonymousIdFromDocumentCookie();
  if (existing) {
    return existing;
  }

  const id = uuidv4();
  document.cookie = buildDustAidCookieString(id);
  return id;
}

/**
 * Reads the `_dust_aid` value from `document.cookie`.
 * Returns `null` if the cookie is not present or we're not in a browser.
 */
function readAnonymousIdFromDocumentCookie(): string | null {
  if (typeof document === "undefined") {
    return null;
  }
  return parseDustAidFromCookieString(document.cookie);
}

/**
 * Server-side helper: parses the `_dust_aid` value from a raw `Cookie` header.
 * Returns `null` if not found.
 */
export function readAnonymousIdFromCookies(
  cookieHeader: string | undefined
): string | null {
  if (!cookieHeader) {
    return null;
  }
  return parseDustAidFromCookieString(cookieHeader);
}

function parseDustAidFromCookieString(cookies: string): string | null {
  const prefix = `${DUST_ANONYMOUS_ID_COOKIE}=`;
  const match = cookies.split("; ").find((c) => c.startsWith(prefix));
  return match ? match.slice(prefix.length) : null;
}
